import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import {
  DRIVER_ORDER_REPORTS_URL,
  REPORT_EXPORT_URL,
} from "../../../config/urls";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useAuth } from "../../../hooks/AuthProvider";
import { useNavigate } from "react-router-dom";
import ButtonOne from "../../../common/normal-button";
import ButtonTwo from "../../../common/dashboard-button";
import AppTable from "../../../common/app-table";
import AppDatePicker from "../../../common/app-date-picker";
import { dateColumn } from '../dateColumn';
import DriverSearchInput from "./SearchInput";
import typography from "../../../cssUtils/typography";

const columns = [
  { id: "driver_name", label: "Driver Name" },
  { id: "zone_name", label: "Zone" },
  { id: "completed_orders_count", label: "Count" },
];

function OrdersTable({ refreshKey, searchInput, updateSearchValue }) {
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const navigate = useNavigate();

  const [rowCount, setRowCount] = useState(0);
  const [ordersData, setOrdersData] = useState([]);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Date filters apply on Search/Reset; the search box applies as soon as an option is picked.
  const [appliedFilters, setAppliedFilters] = useState({});
  const [searchBoxKey, setSearchBoxKey] = useState(0);

  // Same filters for the table and the export.
  const filterParams = () => {
    let params = "";
    if (searchInput) params += `&search_id=${encodeURIComponent(searchInput)}`;
    if (appliedFilters.startDate)
      params += `&start_date=${dayjs(appliedFilters.startDate).format("YYYY-MM-DD")}`;
    if (appliedFilters.endDate)
      params += `&end_date=${dayjs(appliedFilters.endDate).format("YYYY-MM-DD")}`;
    return params;
  };

  const fetchOrders = async (isCancelled) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    const offset = controller.page * controller.rowsPerPage;
    const searchURL = filterParams();
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };
      const response = await fetch(
        `${DRIVER_ORDER_REPORTS_URL}?limit=${controller.rowsPerPage}&offset=${offset}${searchURL}`,
        { headers },
      );
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === "user_inactive") {
          auth.logOut();
          navigate("/login");
        }
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();
      if (isCancelled()) return;
      setOrdersData(jsonData.results);
      setRowCount(jsonData.count);
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  // Single fetch path: every page change and filter change refetches with all filters,
  // and responses from superseded requests are dropped.
  useEffect(() => {
    let cancelled = false;
    fetchOrders(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [controller, appliedFilters, searchInput, refreshKey]);

  useEffect(() => {
    setController((p) => (p.page ? { ...p, page: 0 } : p));
  }, [searchInput]);

  const applyFilters = (filters) => {
    setAppliedFilters(filters);
    setController((p) => ({ ...p, page: 0 }));
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    updateSearchValue("");
    setSearchBoxKey((k) => k + 1);
    applyFilters({});
  };

  const handleExport = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    try {
      const response = await fetch(`${REPORT_EXPORT_URL}?model=driver${filterParams()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (!response.ok) throw new Error("Failed to export data");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const contentDisposition = response.headers.get("Content-Disposition");
      a.download = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "") ||
          "report.csv"
        : "report.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting data:", error.message);
    }
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          py: 1,
          my: 1,
          bgcolor: "background.paper",
          borderRadius: 1,
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <DriverSearchInput key={searchBoxKey} updateSearchValue={updateSearchValue} />
        </Box>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
            <Box sx={{ flex: { xs: 1, sm: '0 0 258px' }, minWidth: 0 }}>
              <AppDatePicker
                value={startDate}
                onChange={(date) => setStartDate(date)}
                placeholder="Start Date"
              />
            </Box>
            <Box sx={{ flex: { xs: 1, sm: '0 0 258px' }, minWidth: 0 }}>
              <AppDatePicker
                value={endDate}
                onChange={(date) => setEndDate(date)}
                placeholder="End Date"
                minDate={startDate || undefined}
              />
            </Box>
          </Box>
          <ButtonOne
            variant="contained"
            size="large"
            onClick={() => applyFilters({ startDate, endDate })}
          >
            Search
          </ButtonOne>
          <ButtonTwo variant="outlined" size="large" onClick={handleReset}>
            Reset
          </ButtonTwo>
          <ButtonOne variant="contained" size="large" onClick={handleExport}>
            Export
          </ButtonOne>
        </LocalizationProvider>
      </Box>

      <AppTable
        columns={[...columns, dateColumn]}
        rows={ordersData}
        rowKey={(o, i) => i}
        emptyMessage="No data found"
        maxHeight={440}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={(e, newPage) =>
          setController((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(e) =>
          setController((prev) => ({
            ...prev,
            rowsPerPage: parseInt(e.target.value, 20),
            page: 0,
          }))
        }
      />
    </Paper>
  );
}

export default OrdersTable;
